import { useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';

const TRUNCATED_CELL = '.table-cell-truncate';

/**
 * Shows the full text of any `.table-cell-truncate` element on hover, but only when the ellipsis cuts it off.
 * Mounted once for the whole app, so no table has to wrap its cells.
 */
export function TruncatedCellTooltip() {
	const [target, setTarget] = useState<HTMLElement | null>(null);

	useEffect(() => {
		const handleOver = (event: MouseEvent) => {
			const cell = (event.target as Element | null)?.closest?.(TRUNCATED_CELL);

			if (cell instanceof HTMLElement && cell.scrollWidth > cell.clientWidth && cell.textContent?.trim()) {
				setTarget(cell);
			}
		};
		// Moving between the cell's own children fires mouseout too; only leaving the cell closes it.
		const handleOut = (event: MouseEvent) => {
			setTarget((current) => (current && !current.contains(event.relatedTarget as Node | null) ? null : current));
		};
		// A click may navigate away and unmount the cell before mouseout fires.
		const close = () => setTarget(null);

		document.addEventListener('mouseover', handleOver);
		document.addEventListener('mouseout', handleOut);
		document.addEventListener('pointerdown', close);
		window.addEventListener('scroll', close, true);

		return () => {
			document.removeEventListener('mouseover', handleOver);
			document.removeEventListener('mouseout', handleOut);
			document.removeEventListener('pointerdown', close);
			window.removeEventListener('scroll', close, true);
		};
	}, []);

	const open = target !== null && target.isConnected;

	return (
		<Tooltip
			title={open ? target.textContent?.trim() : ''}
			open={open}
			placement="top"
			disableInteractive
			slotProps={{ popper: { anchorEl: target } }}
		>
			{/* MUI Tooltip needs a child to mount; the popper is anchored to the hovered cell instead. */}
			<span
				aria-hidden
				style={{ display: 'none' }}
			/>
		</Tooltip>
	);
}
